import { CommonModule } from '@angular/common';
import { Component, OnInit, Output, EventEmitter, Input } from '@angular/core';
import {
  FormBuilder,
  FormGroup,
  Validators,
  ReactiveFormsModule,
  FormsModule,
} from '@angular/forms';
import { ProjectsService } from '@/app/services/projects/projects.service';
import { NgToastService } from 'ng-angular-popup';
import { Router } from '@angular/router';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatSelectModule } from '@angular/material/select';
import { MatFormFieldModule } from '@angular/material/form-field';
import { RichTextEditorComponent } from '@/app/components/shared/rich-textEditor/rich-textEditor.component';
import { GetProjectIdService } from '@/app/services/getProjectId.service';
import {
  PlanStep,
  ProgressService,
} from '@/app/services/shared/progress.service';
import { DocumentService } from '@/app/services/shared/document.service';
import { RiskAssessmentComponent } from '../risk-assessment.component';
import { MatDatepickerModule } from '@angular/material/datepicker';
import { provideNativeDateAdapter } from '@angular/material/core';
import { DialogUtilityService } from '@/app/services/utility/dialog-utility.service';

@Component({
  selector: 'add-risk',
  providers: [provideNativeDateAdapter()],
  standalone: true,
  imports: [
    MatFormFieldModule,
    MatSelectModule,
    MatInputModule,
    CommonModule,
    FormsModule,
    ReactiveFormsModule,
    MatIconModule,
    RichTextEditorComponent,
    RiskAssessmentComponent,
    MatDatepickerModule,
  ],
  templateUrl: './add-risk.component.html',
  styleUrls: ['./add-risk.component.css'],
})
export class AddRiskComponent implements OnInit {
  @Output() changeToRiskRegister: EventEmitter<boolean> =
    new EventEmitter<boolean>();
  resetRichTextEditor = false;
  isLoading = false;

  form: FormGroup;

  // Define available options
  riskOwners: any[] = [
    { name: 'Project Manager', id: 1 },
    { name: 'Operational Manager', id: 2 },
    { name: 'Workflow Manager', id: 7 },
    { name: 'Data Analyst', id: 3 },
    { name: 'Database Manager', id: 4 },
    { name: 'Finance Manager', id: 5 },
    { name: 'Product Manager', id: 6 },
  ];
  @Input() riskCategories = [];
  riskStatuses: string[] = [
    'Active',
    'In Progress',
    'Rejected',
    'Approved',
    'Unitive',
  ];

  searchTerm = '';

  stage2Progress: any;
  documentLength: any;

  filterStatus(searchTerm: string): string[] {
    return this.riskStatuses.filter((status) =>
      status.toLowerCase().includes(searchTerm.toLowerCase())
    );
  }

  constructor(
    private fb: FormBuilder,
    private projectService: ProjectsService,
    private toast: NgToastService,
    private router: Router,
    private getProjectIdService: GetProjectIdService,
    private progressService: ProgressService,
    private documentService: DocumentService,
    private RiskAssessmentComponent: RiskAssessmentComponent,
    private dialogService: DialogUtilityService
  ) {
    this.form = this.fb.group({
      outline: ['', Validators.required],
      riskCategory: ['', Validators.required],
      likelihood: ['', Validators.required],
      impact: ['', Validators.required],
      mitigationStrategy: ['', Validators.required],
      riskOwner: ['', Validators.required],
      targetResolutionDate: ['', Validators.required],
      riskStatus: ['', Validators.required],
    });
  }

  ngOnInit() {
    // Define the form structure and add validation
    this.form = this.fb.group({
      outline: ['', Validators.required],
      riskCategory: ['', Validators.required],
      likelihood: ['', Validators.required],
      impact: ['', Validators.required],

      mitigationStrategy: ['', Validators.required],
      riskOwner: ['', Validators.required],
      targetResolutionDate: ['', Validators.required],
      riskStatus: ['', Validators.required],
    });

    this.documentService.documentLength$.subscribe((length) => {
      this.documentLength = length;
    });
  }

  preventSubmit(event: KeyboardEvent) {
    if (event.key === 'Enter') {
      event.preventDefault(); // Prevent form submission on Enter key press
    }
  }

  addProjectOutline(content: string) {
    console.log('The value of the risk regsiter is', content);
    this.form.controls['outline'].setValue(content);
  }

  addMitigationStrategy(content: string) {
    this.form.controls['mitigationStrategy'].setValue(content);
  }

  navigatetoPreviousPage() {
    this.RiskAssessmentComponent.closeAddRisk();
    if (this.form.valid) {
      this.onSubmit();
    }
  }

  navigateRiskAssessment() {
    this.router.navigate(['/risk-assessment']);
  }

  onSubmit() {
    // console.log('The form values is:', this.form.value);
    if (this.form.valid) {
      const projectId = this.getProjectIdService.getProjectId();
      if (!projectId) {
        this.toast.danger('Project ID not found', 'DANGER', 4000);
        return;
      }
      const formData = {
        project_id: projectId,
        description: this.form.value.outline,
        risk_category_id: this.form.value.riskCategory,
        likelihood: this.form.value.likelihood,
        impact: this.form.value.impact,
        mitigation_strategy: this.form.value.mitigationStrategy,
        risk_owner: this.form.value.riskOwner,
        target_resolution_date: this.form.value.targetResolutionDate,
        status: this.form.value.riskStatus,
      };
      this.isLoading = true;

      this.projectService.addRisk(formData).subscribe({
        next: () => {
          this.toast.success(
            'Risk assessment added successfully',
            'SUCCESS',
            4000
          );
          this.progressService.updateStepProgress(
            PlanStep.RiskAssessment,
            true
          );
          this.RiskAssessmentComponent.closeAddRisk();
          this.isLoading = false;

          this.changeToRiskRegister.emit(true);
        },
        error: (error: any) => {
          console.error('Error while adding risk assessment:', error);
          this.isLoading = false;

          this.toast.danger(
            'Error while adding risk assessment',
            'DANGER',
            4000
          );
        },
      });
    } else {
      // Find invalid fields and display a message
      this.form.markAllAsTouched();
      const invalidFields: string[] = [];
      Object.keys(this.form.controls).forEach((key) => {
        const control = this.form.get(key);
        if (control?.invalid) {
          invalidFields.push(key);
        }
      });

      this.toast.danger(`Please fill the required fields!`, 'DANGER', 4000);
    }
  }
  closeDialog() {
    this.dialogService.closeProjectPlanDialog();
    if (this.form.valid) {
      this.onSubmit();
    }
  }
}
